import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { App, Divider, Flex, Pagination, Space, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { connect, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"

import Button from "../../Components/Button"
import EmptyComponent from "../../Components/Empty"
import ExportDropdown from "../../Components/Export"
import Filters from "../../Components/Filters"
import GlobalFilterTestData from "../../Components/GlobalFilterTestData/globalFilterTest"
import ManageColumn from "../../Components/ManageColumns"
import { CommentModal } from "../../Components/Modal/CommentModal"
import Table, { itemRender } from "../../Components/Table"
import { getBaseColumns } from "../../Components/Table/baseColumns"
import Icon from "../../Icon"
import { modalConfig } from "../../theme"
import { adminRoleId } from "../../Utils/permission"
import { serializeData, usersColumnIconMap } from "../../Utils/utils"
import GlobalCreators from "../GlobalFilters/reducer"
import { GiftModal } from "./components/GiftModal"
import { SwitchToLoyalBillingModal } from "./components/SwitchToLoyalBillingModal"
import { getActionColumn, getColumns, getDefaultColumn } from "./components/Table/columns"
import { WithdrowalModal } from "./components/WithdrowalModal"
import Creators, { selectAreFiltersDefault } from "./reducer"

import "./styles.less"

const pageSize = 50

const pinnedColumns = ["#", "Status"]

const { Title, Text } = Typography

const UsersPage = ({
  isMobile,
  roleId,
  visibleFiltersAdmin,
  defaultColumnsAdmin,
  columnsAdmin,
  fetching,
  result,
  filters,
  filterOptions,
  exportFetching,
  exportResult,
  statistics,
  saveResult,
  test_data,
  requestResult,
  userInfoComment,
  userCommentData,
  userBusinessAccountsList,
  commentsFetching,
  ...props
}) => {
  const { t } = useTranslation()
  const { notification, modal } = App.useApp()

  const navigate = useNavigate()
  const [pageNumber, setPageNumber] = useState(1)
  const [ordering, setOrdering] = useState(null)
  const [modalType, setModalType] = useState(null)

  const isFiltersDefault = useSelector(selectAreFiltersDefault)

  const { count, results } = result

  const myColumns = columnsAdmin
  const allFilters = defaultColumnsAdmin
  const visibleFilters = visibleFiltersAdmin

  const myColumnsLength = myColumns.filter(({ active }) => active).length
  const myColumnsHash = myColumns
    .filter(({ active }) => active)
    .map(({ field_id }) => field_id)
    .join("")

  const notificationConfig = {
    closeIcon: <Text>{t("Dismiss")}</Text>,
    style: { minWidth: "max-content", width: 475 }
  }

  useEffect(() => {
    document.title = "Users - Chargeback"
    props.getUsersFilterOptions()

    return () => {
      props.clearUserCommentsData()
    }
  }, [])

  const updateData = () => {
    const serializeFilters = serializeData(filters)
    const columns = myColumns.filter(({ active }) => active).map(({ field_id }) => field_id)
    const data = {
      page: pageNumber,
      page_size: pageSize,
      ordering,
      ...serializeFilters,
      test_data,
      ...(columns.length && { columns: ["sequence_number", "id", "status", ...columns].join(",") })
    }
    props.getUsers({ ...data })
  }

  const prevTestDataRef = useRef(test_data)

  useEffect(() => {
    const testDataChanged = prevTestDataRef.current !== test_data
    if (testDataChanged) {
      prevTestDataRef.current = test_data
      if (pageNumber !== 1) {
        setPageNumber(1)
        return
      }
    }

    updateData()
  }, [ordering, pageNumber, filters, test_data, myColumnsLength])

  useEffect(() => {
    if (exportResult) {
      notification.success({
        message: "Exporting",
        description: "Users will be downloaded shortly.",
        ...notificationConfig
      })
      props.clearExportResult()
    }
  }, [exportResult])

  useEffect(() => {
    if (saveResult) {
      if (saveResult === "user_delete") {
        updateData()
      }
      if (saveResult?.type === "giftRequest" || saveResult?.type === "withdrawRequest") {
        setModalType(null)
        notification.success({
          message:
            saveResult?.type === "giftRequest"
              ? saveResult?.data?.message || "Gift successfully sent"
              : saveResult?.data?.message || "Withdrawal created1",
          // description: '',
          ...notificationConfig
        })
      }
      props.saveResultReset()
    }
  }, [saveResult])

  const tableSettings = useMemo(
    () => ({
      scroll: {
        //x: (230 * myColumns.filter(({active}) => active).length),
        x: "max-content",
        y: "calc(100vh - 72px - 24px - 32px - 24px - 43px - (16px + 32px + 16px))",
        scrollToFirstRowOnChange: true
      }
      /*pagination: {
        total: count,
        current: pageNumber,
        pageSize: pageSize,
      }*/
    }),
    [myColumnsLength, count, pageNumber]
  )

  const getTableColumns = useMemo(() => {
    let allColumns = {
      ...getBaseColumns({
        t,
        isAdmin: true,
        onSelectRow: handleSelectRow,
        sorted: { value: false }
      }),
      ...getColumns({ data: results || [], onSelectRow: handleSelectRow })
    }

    return [
      allColumns["sequence_number"],
      allColumns["status"],
      ...myColumns
        .filter(({ active }) => active)
        .map((data) => allColumns?.[data.field_id] || getDefaultColumn({ ...data, t })),
      getActionColumn({ onSelectRow: handleSelectRow, t, data: results || [] })
    ]
  }, [myColumnsHash, results])

  const handleChangeTable = useCallback((pagination, filters, sorter) => {
    let ordering = null
    if (sorter) {
      if (Array.isArray(sorter)) {
        ordering = sorter
          ?.map(({ field, order }) => (order === "ascend" ? field : `-${field}`))
          .join(",")
      } else {
        if (sorter.order) {
          ordering = sorter.order === "ascend" ? sorter.field : `-${sorter.field}`
        }
      }
    }
    setPageNumber(pagination.current)
    setOrdering(ordering)
  }, [])

  const handleChangeFilter = useCallback((newData, type) => {
    if (newData?.user_email) {
      if (
        newData?.user_email &&
        Array.isArray(newData.user_email) &&
        newData.user_email.length > 0
      ) {
        newData.user_email.forEach((email) => {
          if (email) props.addGlobalFilter("user_email", email.trim())
        })
      } else if (typeof newData.user_email === "string" && newData.user_email.trim()) {
        props.addGlobalFilter("user_email", newData.user_email.trim())
      } else {
        props.removeGlobalFilter("user_email")
      }
    }
    if (type === "changeVisible") {
      props.changeVisibleFilters("visibleFiltersAdmin", newData)
    } else {
      props.changeFilters(newData)
      setPageNumber(1)
    }
  }, [])

  const onClear = useCallback(() => {
    props.clearUsersFilters()
    props.clearGlobalFilters()
  }, [])

  const handleChangeColumns = useCallback((newColumns) => {
    props.changeColumns("columnsAdmin", newColumns)
  }, [])

  function handleSelectRow(userData, type) {
    let business_account_id =
      userData?.business_account?.business_account_id || userData?.business_account?.id
    if (type === "business_account") {
      if (business_account_id) {
        window.open(
          `${process.env.REACT_APP_BASE_URI}/business_settings/enrollment?${new URLSearchParams({ business_account_id })}`
        )
      }
    } else if (type === "dashboard") {
      navigate(`/admin/dashboard?business_account_id=${business_account_id}`)
    } else if (type === "delete") {
      modal.confirm({
        ...modalConfig("exclamation"),
        centered: !isMobile,
        title: "Confirm deleting",
        content:
          "Are you sure you want to delete this user and corresponding business accounts? This action can NOT be undone.",
        cancelText: "Cancel",
        okText: "Delete",
        okButtonProps: {
          ...modalConfig("exclamation")?.okButtonProps,
          danger: true,
          ghost: true
        },
        onOk() {
          props.deleteUser(userData?.id)
        },
        onCancel() {
          console.log("Cancel")
        }
      })
    } else if (type === "switch_to_loyal_billing") {
      setModalType({ type, record: userData })
    } else if (type === "user_balance") {
      navigate(`/settings/balance?user_id=${userData?.id}`, { state: { from: "/users" } })
    } else if (["withdraw", "gift"].includes(type)) {
      setModalType({ type, balance_id: userData?.balance_id })
    } else if (type === "settings") {
      navigate(`/settings/balance?user_id=${userData?.id}`)
    } else if (["withdraw", "gift", "comments_users_page"].includes(type)) {
      setModalType({
        type,
        user_id: userData?.id
      })
      props.getCommentsUserInfo({ user_id: userData?.id })
      props.getUserBusinessAccountsList({ user_id: userData?.id })
      if (modalTabKey === "user_business_accounts" && currentBA) {
        props.getUserComment({ user_id: userData?.id, ba_id: currentBA })
      } else {
        props.getUserComment({ user_id: userData?.id })
      }
    }
  }

  const handleExport = useCallback(
    (type) => {
      const serializeFilters = serializeData(filters)
      props.getUsersExport({
        file_name: "Users report",
        ...(type !== "all" && serializeFilters),
        ...(type !== "all" ? { test_data } : {})
      })
    },
    [filters, test_data]
  )

  //actions for commentModal
  const formRef = useRef(null)
  const [modalTabKey, setModalTabKey] = useState("user_comment")
  const [currentBA, setCurrentBA] = useState(null)

  // Switching tabs re-mounts CommentForm before the new tab's comment has actually loaded,
  // so without this it briefly (and, depending on how fast the fetch resolves, unpredictably)
  // shows the previous tab's still-cached userCommentData instead of a blank/loading field.
  const handleModalTabChange = (key) => {
    setModalTabKey(key)
    props.resetUserCommentData()
  }

  const onSaveFormSuccess = () => {
    setModalType(false) //close modal
    // Match onClose: without this, modalTabKey/currentBA carry over to the next time this
    // modal opens (for this user or another), so it can reopen already pointed at the BA tab
    // with a stale currentBA and immediately (mis)fetch that comment.
    setModalTabKey("user_comment")
    setCurrentBA(null)
    props.clearUserCommentsData()
    notification.success({
      message: "Save Comment",
      description: "Comment has been saved successfully.",
      ...notificationConfig
    })
  }

  const onCommentDeleteSuccess = () => {
    notification.success({
      message: "Delete Comment",
      description: "Comment has been deleted successfully.",
      ...notificationConfig
    })
    formRef.current?.setFieldsValue({ comment: "" })
  }

  const onDeleteCommentAttachmentSuccess = () => {
    notification.success({
      message: "Delete Comment",
      description: "Comment has been deleted successfully.",
      ...notificationConfig
    })
    props.getUserComment({ user_id: modalType?.user_id })
  }

  const handleSaveCommentForm = useCallback(
    (data, type) => {
      const newData = {
        ...data,
        user_id: userCommentData?.user_id ?? userCommentData?.user ?? modalType?.user_id,
        comment_id: userCommentData?.id,
        onSuccess: onSaveFormSuccess,
        ...(type?.tabKeyType === "user_business_accounts" && { ba_id: currentBA })
      }
      props.saveComment(newData)
    },
    [
      userCommentData?.user_id,
      userCommentData?.user,
      userCommentData?.id,
      modalType?.user_id,
      currentBA
    ]
  )

  const onCommentDelete = useCallback(
    (data, type) => {
      props.deleteUserComment({
        user_id: data?.user_id ?? data?.user ?? modalType?.user_id,
        onSuccess: onCommentDeleteSuccess,
        ...(type?.tabKeyType === "user_business_accounts" && { ba_id: currentBA })
      })
    },
    [currentBA, modalType?.user_id]
  )

  const onDeleteAttachment = (data) => {
    props.deleteUserCommentAttachment({
      id: data?.id, //attachment_id
      user_id: userCommentData?.user_id ?? userCommentData?.user ?? modalType?.user_id,
      comment_id: userCommentData?.id,
      onSuccess: onDeleteCommentAttachmentSuccess
    })
  }

  useEffect(() => {
    if (modalTabKey !== "user_business_accounts" || !userBusinessAccountsList?.length) return
    const isCurrentBAStillValid = userBusinessAccountsList.some((ba) => ba?.id === currentBA)
    if (!isCurrentBAStillValid) {
      setCurrentBA(userBusinessAccountsList[0]?.id)
    }
  }, [modalTabKey, userBusinessAccountsList, currentBA])

  useEffect(() => {
    // Depends on modalTabKey too, not just currentBA: revisiting this tab without currentBA
    // having changed (it's already set from an earlier visit) still has to refetch, since
    // switching tabs now eagerly nulls userCommentData (see handleModalTabChange).
    if (modalTabKey === "user_business_accounts" && currentBA) {
      props.getUserComment({ user_id: modalType?.user_id, ba_id: currentBA })
    }
  }, [modalTabKey, currentBA])

  // Symmetric to the effect above: switching back to the user-level tab has to refetch too,
  // otherwise it keeps showing whatever the business-account tab last loaded into the shared
  // userCommentData.
  useEffect(() => {
    if (modalTabKey === "user_comment" && modalType?.user_id) {
      props.getUserComment({ user_id: modalType.user_id })
    }
  }, [modalTabKey, modalType?.user_id])

  return (
    <div className="users-page">
      <Title level={3} style={{ textAlign: "left", marginBottom: 16 }}>
        {`Users: ${statistics?.["users_counter"]}`}
      </Title>

      <Divider />

      <Flex gap={32} align="center" justify="space-between" style={{ margin: "-4px 0 20px" }}>
        <Filters
          visibleFilters={visibleFilters}
          filterOptions={filterOptions}
          allFilters={allFilters}
          filters={filters}
          onChangeFilter={handleChangeFilter}
          showClearBtn={!isFiltersDefault}
          onClearFilters={onClear}
          addGlobalFilter={props.addGlobalFilter}
          removeGlobalFilter={props.removeGlobalFilter}
          columnIconMap={usersColumnIconMap}
        />

        <Space style={{ flexShrink: 0 }}>
          <GlobalFilterTestData />
          {roleId !== adminRoleId && (
            <ExportDropdown loading={exportFetching} onExport={handleExport} isAdmin={true} />
          )}

          <ManageColumn
            myColumns={myColumns}
            onChangeColumns={handleChangeColumns}
            pinnedColumns={pinnedColumns}
            iconsType={"users"}
          />
        </Space>
      </Flex>

      <Table
        rowKey={"row_id"}
        columns={getTableColumns}
        loading={fetching}
        dataSource={results}
        scroll={tableSettings.scroll}
        onChange={handleChangeTable}
        //pagination={tableSettings.pagination}
        showSizeChanger={false}
        expandable={{ defaultExpandAllRows: true }}
        withOutPagination={true}
        locale={{
          emptyText: !statistics?.["users_counter"] ? (
            <EmptyComponent type="users_admin" />
          ) : (
            <EmptyComponent type="search">
              <Button
                className="flex"
                icon={<Icon type={"menu_search"} />}
                onClick={props.clearUsersFilters}
              >
                {t("Clear search")}
              </Button>
            </EmptyComponent>
          )
        }}
      />
      <div className={"custom-table"} style={{ margin: "16px 0" }}>
        <Pagination
          rootClassName={"ant-table-pagination ant-table-pagination-left"}
          onChange={(page) => setPageNumber(page)}
          position={["none", "bottomLeft"]}
          total={count}
          pageSize={50}
          current={pageNumber}
          showLessItems={true}
          showTotal={(total, range) => (
            <Text>
              Showing <Text strong>{`${range[0]}-${range[1]} of ${total}`}</Text> results
            </Text>
          )}
          itemRender={(_, type, originalElement) =>
            pageSize >= count ? null : itemRender(_, type, originalElement, t)
          }
          showSizeChanger={false}
          showQuickJumper={false}
        />
      </div>
      {modalType?.type === "withdraw" ? (
        <WithdrowalModal modalType={modalType} setModalType={setModalType} />
      ) : null}
      {modalType?.type === "gift" ? (
        <GiftModal modalType={modalType} setModalType={setModalType} />
      ) : null}
      {modalType?.type === "switch_to_loyal_billing" ? (
        <SwitchToLoyalBillingModal
          modalType={modalType}
          setModalType={setModalType}
          onSuccess={() => {
            updateData()
            notification.success({
              message: "User switched to Loyal Billing",
              ...notificationConfig
            })
          }}
        />
      ) : null}
      {modalType?.type === "comments_users_page" ? (
        <CommentModal
          modalType={modalType}
          setModalType={setModalType}
          modalTabKey={modalTabKey}
          setModalTabKey={handleModalTabChange}
          handleSaveForm={handleSaveCommentForm}
          onCommentDelete={onCommentDelete}
          onDeleteAttachment={onDeleteAttachment}
          formRef={formRef}
          userInfoComment={userInfoComment}
          userCommentData={userCommentData}
          userBusinessAccountsList={userBusinessAccountsList}
          setCurrentBA={setCurrentBA}
          commentDataLoading={commentsFetching}
          onClose={() => {
            setModalTabKey("user_comment")
            props.clearUserCommentsData()
          }}
        />
      ) : null}
    </div>
  )
}

const mapStateToProps = (state) => ({
  isMobile: state.nav.isMobile,
  roleId: state.auth.userInfo?.role?.id,

  visibleFiltersAdmin: state.users.visibleFiltersAdmin,

  defaultColumnsAdmin: state.users.defaultColumnsAdmin,

  columnsAdmin: state.users.columnsAdmin,

  fetching: state.users.fetching,
  result: state.users.result,

  filters: state.users.filters,
  filterOptions: state.users.filterOptions,

  exportFetching: state.users.exportFetching,
  exportResult: state.users.exportResult,
  saveResult: state.users.saveResult,

  userInfoComment: state.users.userInfoComment,
  userCommentData: state.users.userCommentData,
  userBusinessAccountsList: state.users.userBusinessAccountsList,
  commentsFetching: state.users.commentsFetching,

  statistics: state.businessAccounts.statistics,
  test_data: state.globalFilters.testData
})

const mapDispatchToProps = (dispatch) => ({
  getUsersFilterOptions: () => dispatch(Creators.getUsersFilterOptionsRequest()),

  getUsers: (params) => dispatch(Creators.getUsersRequest(params)),
  deleteUser: (id) => dispatch(Creators.deleteUserRequest(id)),
  deleteUserComment: (id) => dispatch(Creators.deleteUserCommentRequest(id)),
  getUsersExport: (params) => dispatch(Creators.getUsersExportRequest(params)),
  clearExportResult: () => dispatch(Creators.usersSetProp("exportResult", null)),
  changeColumns: (key, columns) => dispatch(Creators.usersSetProp(key, columns)),
  changeFilters: (filters) => dispatch(Creators.usersSetProp("filters", filters)),
  changeVisibleFilters: (visibleFiltersType, filters) =>
    dispatch(Creators.usersSetProp(visibleFiltersType, filters)),
  saveResultReset: () => dispatch(Creators.usersSetProp("saveResult", null)),
  clearUsersFilters: () => dispatch(Creators.clearUsersFilters()),
  clearGlobalFilters: () => dispatch(GlobalCreators.clearGlobalFilters()),
  addGlobalFilter: (key, value) => dispatch(GlobalCreators.addGlobalFilter(key, value)),
  removeGlobalFilter: (key) => dispatch(GlobalCreators.removeGlobalFilter(key)),

  getCommentsUserInfo: (params) => dispatch(Creators.getUserInfoCommentRequest(params)),
  getUserBusinessAccountsList: (params) =>
    dispatch(Creators.getUserBusinessAccountsListRequest(params)),
  getUserComment: (params) => dispatch(Creators.getUserCommentRequest(params)),
  resetUserCommentData: () => dispatch(Creators.usersSetProp("userCommentData", null)),
  saveComment: (params) => dispatch(Creators.saveCommentRequest(params)),
  deleteUserCommentAttachment: (params) =>
    dispatch(Creators.deleteUserCommentAttachmentRequest(params)),
  clearUserCommentsData: () => dispatch(Creators.clearUserCommentsData())
})

export default connect(mapStateToProps, mapDispatchToProps)(UsersPage)
